// src/components/demo/DemoSpotSetup.js
//
// Demo-day selection + (admin-only) creation. Nigel 2026-07-22:
//   - ONLY admins create demo days (location, date, optional start time) and
//     configure offers: a default WAKA discount code, plus per-retailer offers
//     (retailer from the catalogue dropdown, the sizes they stock, their code).
//     Riders get routed in the ride email by the size they rode.
//   - Staff (WAKA + partner) pick from the admin-created upcoming days.
// The retailer catalogue (name + per-size board URLs) is admin-maintained here
// too ("+ add retailer") and reused across events.

import React, { useEffect, useMemo, useState } from 'react';
import { FScreen, FHeader, FHeadline, FBtn, FLabel } from '../foam/FoamPrimitives';
import {
  watchUpcomingDemoDays, createDemoDay, updateDemoDay, deleteDemoDay,
  listRetailers, saveRetailer, isPastDay, formatDayDate, formatDayRange, dayCount,
  listSeasonSpotsFor, openTodayAtSpot,
} from '../../services/demoService';
import DemoSeasonSetup from './DemoSeasonSetup';

const GLIDE_SIZES = ['66', '77', '88', '99', '111'];

const todayStr = () => {
  const d = new Date(); const p2 = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
};

const Input = (props) => (
  <input
    {...props}
    className="w-full font-sans text-[16px] px-[14px]"
    // minWidth: 0 + maxWidth: 100% — iOS gives date/time inputs an intrinsic
    // min width that ignores w-full and pushed the whole page into horizontal
    // scroll (Nigel 2026-07-23). Paired with the appearance reset in index.css.
    style={{ height: 52, minWidth: 0, maxWidth: '100%', border: '1.5px solid var(--foam-line)', borderRadius: 0, background: '#FFFFFF', ...props.style }}
  />
);

// Design r2 (D4Sizes): selected = filled carbon; selected-but-missing-URL = warn orange.
const SizeChips = ({ selected, warn = [], onToggle }) => (
  <div className="flex gap-[7px]">
    {GLIDE_SIZES.map((sz) => {
      const on = selected.includes(sz);
      const bad = on && warn.includes(sz);
      return (
        <button
          key={sz}
          type="button"
          onClick={() => onToggle(sz)}
          className="flex-1 font-mono text-[14px] font-bold text-center"
          style={{
            minHeight: 44, letterSpacing: '0.02em',
            border: `1.5px solid ${on ? (bad ? 'var(--foam-orange)' : 'var(--foam-line)') : 'var(--foam-hair)'}`,
            background: on ? (bad ? '#FFF7F3' : 'var(--foam-carbon)') : 'var(--foam-paper)',
            color: on ? (bad ? 'var(--foam-orange)' : 'var(--foam-paper)') : 'var(--foam-mute)',
          }}
        >
          {sz}
        </button>
      );
    })}
  </div>
);

const Section = ({ label, hint, children }) => (
  <div>
    <div className="mb-[12px]"><FLabel>{label}</FLabel></div>
    {children}
    {hint && <p className="font-sans text-[12px] mt-[8px]" style={{ color: 'var(--foam-ink-2)', lineHeight: 1.5 }}>{hint}</p>}
  </div>
);

export default function DemoSpotSetup({ user, role, onDaySelected, notice, onDismissNotice }) {
  const isAdmin = role === 'admin';
  const [days, setDays] = useState(null);           // null = loading
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null); // eventId being edited (admin)
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Day picked on an AMBIGUOUS date, awaiting an explicit location confirm
  // (round 2 §2). Never set when the date has only one day on it.
  const [pendingDay, setPendingDay] = useState(null);
  // Admin ticked "yes, this date really is in the past" (round 2 §1).
  const [pastDateOk, setPastDateOk] = useState(false);

  // Creator state (admin)
  const [name, setName] = useState('');
  const [date, setDate] = useState(todayStr());
  // Optional last date — a show that runs Wed–Sat is ONE event (Nigel
  // 2026-09-08). Blank = single day, which is every ordinary demo day.
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [defaultCode, setDefaultCode] = useState('');
  const [retailers, setRetailers] = useState([]);
  const [offers, setOffers] = useState([]);         // [{retailerId, name, sizes, discountCode, boardUrlsBySize}]
  const [addingRetailer, setAddingRetailer] = useState(false);
  const [newRetName, setNewRetName] = useState('');
  const [newRetUrls, setNewRetUrls] = useState({});

  // Season demos (2026-09-06): spots this user may open today's day at.
  // Partner staff see only spots that list them; WAKA/admin see all active.
  const [seasonSpots, setSeasonSpots] = useState([]);
  const [seasonSetup, setSeasonSetup] = useState(false);
  const [openingId, setOpeningId] = useState(null);

  // Live list (2026-07-23 duplicate-day incident): every device sees creates,
  // moves and deletes immediately — no stale rows to edit from.
  useEffect(() => watchUpcomingDemoDays(setDays), []);
  useEffect(() => { if (isAdmin) listRetailers().then(setRetailers).catch(() => {}); }, [isAdmin]);
  useEffect(() => {
    if (!user || !role) return;
    listSeasonSpotsFor(user, role).then(setSeasonSpots).catch(() => setSeasonSpots([]));
  }, [user, role, seasonSetup]);

  // Open (or re-join) today's day at a season spot. The callable is
  // idempotent, so a second phone on the same day lands on the same eventId —
  // never a parallel fleet (2026-08-02).
  const openToday = async (s) => {
    setOpeningId(s.id); setError(null);
    try {
      const day = await openTodayAtSpot(s.id);
      onDaySelected({ id: day.id, spotId: day.spotId, spotName: day.spotName, date: day.date });
    } catch (e) {
      setError(e.message || `Could not open today at ${s.name}.`);
      setOpeningId(null);
    }
  };

  // Group by date so same-date days read as "two events, this day, pick which"
  // rather than two near-identical rows (round 2 §2). `days` arrives sorted by
  // date then start time, so insertion order is already correct.
  const dayGroups = useMemo(() => {
    const byDate = new Map();
    (days || []).forEach((d) => {
      if (!byDate.has(d.date)) byDate.set(d.date, []);
      byDate.get(d.date).push(d);
    });
    return [...byDate.entries()].map(([date, items]) => ({
      date, items, ambiguous: items.length > 1,
    }));
  }, [days]);

  // Ambiguous date → confirm the location before entering the scan loop.
  // Unambiguous → straight through, no extra tap.
  const pickDay = (d, ambiguous) => {
    if (ambiguous) { setPendingDay(d); return; }
    onDaySelected(d);
  };

  const addOffer = (retailer) => {
    if (!retailer || offers.some((o) => o.retailerId === retailer.id)) return;
    // Seeds come from the web store's retailer list (config/demoRetailers.js).
    // First use materialises the seed into the Firestore catalogue so it
    // becomes admin-editable like any hand-entered retailer.
    if (retailer.seed) {
      saveRetailer({ id: retailer.id, name: retailer.name, boardUrlsBySize: retailer.boardUrlsBySize }, user)
        .catch(() => {});
    }
    setOffers([...offers, {
      retailerId: retailer.id, name: retailer.name,
      boardUrlsBySize: retailer.boardUrlsBySize || {},
      sizes: [], discountCode: '',
    }]);
  };

  // Fresh create — clear anything left over from an edit. Without this the
  // form opens showing the last-edited day's values, and since 2026-09-08 that
  // includes its end date, which would silently make the new day multi-day.
  const startCreate = () => {
    setName(''); setDate(todayStr()); setEndDate('');
    setStartTime(''); setEndTime(''); setDefaultCode(''); setOffers([]);
    setError(null); setConfirmDelete(false); setPastDateOk(false);
    setEditingId(null); setCreating(true);
  };

  // Admin taps EDIT on a day row → creator form, prefilled (Nigel 2026-07-23).
  const startEdit = (d) => {
    setName(d.spotName || '');
    setDate(d.date || todayStr());
    setEndDate(d.endDate || '');
    setStartTime(d.startTime || '');
    setEndTime(d.endTime || '');
    setDefaultCode((d.offers && d.offers.default && d.offers.default.discountCode) || '');
    setOffers(((d.offers && d.offers.retailers) || []).map((o) => ({
      retailerId: o.retailerId, name: o.name,
      boardUrlsBySize: o.boardUrlsBySize || {},
      sizes: o.sizes || [], discountCode: o.discountCode || '',
    })));
    setError(null);
    setConfirmDelete(false);
    setPastDateOk(false);
    setEditingId(d.id);
    setCreating(true);
  };

  const create = async () => {
    // Backfilling a missed day is legitimate, so a past date stays possible —
    // but it must be deliberate, not the result of tapping through the date
    // field (round 2 §1). `ready` gates the button; this is the backstop.
    if (isPastDay({ date, endDate }) && !pastDateOk) {
      setError('That date is in the past — tick the confirm below if you meant it.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const payload = {
        name, date, endDate: endDate || null,
        startTime: startTime || null, endTime: endTime || null,
        offers: {
          default: { discountCode: defaultCode.trim() || null },
          retailers: offers
            .filter((o) => o.sizes.length > 0)
            .map((o) => ({
              retailerId: o.retailerId, name: o.name, sizes: o.sizes,
              discountCode: o.discountCode.trim() || null,
              boardUrlsBySize: o.boardUrlsBySize,
            })),
        },
      };
      const { id, spot } = editingId
        ? await updateDemoDay(editingId, payload, user)
        : await createDemoDay(payload, user);
      // Carry endDate through — without it the phone that CREATED a multi-day
      // event holds a day object that looks single-day until the next reload,
      // and would expire itself at midnight mid-show.
      onDaySelected({ id, spotId: spot.id, spotName: spot.name, date, endDate: endDate || null }, spot);
    } catch (e) {
      setError(e.message || (editingId ? 'Could not save the demo day.' : 'Could not create the demo day.'));
      setBusy(false);
    }
  };

  // ── Ambiguous date: name the location in full before the scan loop ──
  if (pendingDay && !creating) {
    return (
      <FScreen
        header={<FHeader right={<FLabel color="var(--foam-orange)">Two demo days today</FLabel>} />}
        footer={
          <>
            <FBtn primary onClick={() => onDaySelected(pendingDay)}>Confirm — start scanning</FBtn>
            <FBtn ghost onClick={() => setPendingDay(null)}>Choose again</FBtn>
          </>
        }
      >
        <FHeadline size={40}>You&apos;re<br />running</FHeadline>
        <div style={{ border: '1.5px solid var(--foam-line)', padding: 20 }}>
          <FHeadline size={34} className="!normal-case">
            <FHeadline.Accent block>{pendingDay.spotName}</FHeadline.Accent>
          </FHeadline>
          <div className="mt-[10px]">
            <FLabel>{formatDayRange(pendingDay)}</FLabel>
          </div>
          {(pendingDay.startTime || pendingDay.endTime) && (
            <div className="mt-[4px]">
              <FLabel color="var(--foam-mute)">
                {pendingDay.startTime || ''}{pendingDay.endTime ? `–${pendingDay.endTime}` : ''}
              </FLabel>
            </div>
          )}
        </div>
        <p className="font-sans text-[14px]" style={{ color: 'var(--foam-ink-2)', lineHeight: 1.55 }}>
          More than one demo day is running on this date. Everything you scan, check out and capture
          is filed under the location above — check it matches the other staff phone before you start.
        </p>
      </FScreen>
    );
  }

  // ── Admin: season locations (ambassador-run spots) ──
  if (seasonSetup && isAdmin) {
    return <DemoSeasonSetup user={user} onBack={() => setSeasonSetup(false)} />;
  }

  // ── Staff (and admin default view): pick an upcoming day ──
  if (!creating) {
    const todayKey = todayStr();
    return (
      <FScreen
        header={<FHeader right={<FLabel>Demo days</FLabel>} />}
        footer={isAdmin
          ? (
            <>
              <FBtn primary onClick={startCreate}>Create a demo day</FBtn>
              <FBtn ghost onClick={() => setSeasonSetup(true)}>Season locations</FBtn>
            </>
          )
          : (
            <p className="font-sans text-[12px] text-center" style={{ color: 'var(--foam-mute)' }}>
              Demo days are set up by WAKA — ask your WAKA contact if today's isn't listed.
            </p>
          )}
      >
        <FHeadline size={44}>Which<br /><FHeadline.Accent>demo day?</FHeadline.Accent></FHeadline>

        {/* Season spots (2026-09-06): one tap opens — or re-joins — today's
            day at a spot this staffer is listed on. Shown above the list so an
            ambassador never has to scroll or wait for WAKA. */}
        {seasonSpots.map((s) => {
          const already = (days || []).some((d) => d.spotId === s.id && d.date === todayKey);
          return (
            <div key={s.id} style={{ border: '1.5px solid var(--foam-carbon)', padding: 16 }}>
              <FLabel color="var(--foam-mute)">Season demos{s.season?.ambassadorName ? ` · ${s.season.ambassadorName}` : ''}</FLabel>
              <div className="font-display font-extrabold uppercase text-[26px] mt-[4px]"
                style={{ letterSpacing: '-0.01em', lineHeight: 1.05 }}>
                {s.name}
              </div>
              <div className="mt-[12px]">
                <FBtn primary height={52} disabled={openingId === s.id} onClick={() => openToday(s)}>
                  {openingId === s.id ? 'Opening…' : (already ? `Join today at ${s.name}` : `Open today at ${s.name}`)}
                </FBtn>
              </div>
              <p className="font-sans text-[12px] mt-[8px]" style={{ color: 'var(--foam-ink-2)', lineHeight: 1.5 }}>
                {formatDayDate(todayKey)}{s.season?.discountCode ? ` · riders get code ${s.season.discountCode}` : ''}
              </p>
            </div>
          );
        })}
        {error && !creating && <FLabel color="var(--foam-orange)">{error}</FLabel>}

        {/* Why the picker is showing, when it's showing because the phone was
            taken off a finished day (round 2 §1). */}
        {notice && (
          <div style={{ border: '1.5px solid var(--foam-orange)', background: '#FFF7F3', padding: 16 }}>
            <div
              className="font-display font-extrabold uppercase text-[18px]"
              style={{ color: 'var(--foam-orange)', letterSpacing: '-0.01em' }}
            >
              That day has finished
            </div>
            <p className="font-sans text-[14px] mt-[6px]" style={{ color: 'var(--foam-ink)', lineHeight: 1.5 }}>
              {notice}
            </p>
            {onDismissNotice && (
              <button
                type="button"
                onClick={onDismissNotice}
                className="font-mono text-[11px] tracking-[0.14em] uppercase underline mt-[10px]"
                style={{ color: 'var(--foam-ink-2)', minHeight: 44 }}
              >
                Dismiss
              </button>
            )}
          </div>
        )}

        {days === null && <FLabel>Loading…</FLabel>}
        {days !== null && days.length === 0 && (
          <p className="font-sans text-[15px]" style={{ color: 'var(--foam-ink-2)' }}>
            No upcoming demo days yet.
          </p>
        )}

        {dayGroups.map(({ date, items, ambiguous }) => (
          <div key={date} className="flex flex-col gap-[8px]">
            {/* On an ambiguous date the DATE becomes the group heading and the
                LOCATION becomes the primary line on each row — the two rows
                previously differed only by a spot name in the same weight and
                colour, which is how you reproduce 1 August exactly. */}
            {ambiguous && (
              <div className="flex justify-between items-end">
                <FLabel color="var(--foam-orange)">{formatDayDate(date)}</FLabel>
                <FLabel color="var(--foam-orange)">{items.length} locations — pick one</FLabel>
              </div>
            )}
            {items.map((d) => (
              <div key={d.id} className="flex items-stretch" style={{ border: '1.5px solid var(--foam-line)' }}>
                <button
                  type="button"
                  className="text-left flex-1"
                  style={{ minHeight: 64, minWidth: 0, padding: '14px 16px' }}
                  onClick={() => pickDay(d, ambiguous)}
                >
                  {ambiguous ? (
                    <>
                      <div
                        className="font-display font-extrabold uppercase text-[26px]"
                        style={{ letterSpacing: '-0.01em', lineHeight: 1.05 }}
                      >
                        {d.spotName}
                      </div>
                      <div className="mt-[3px]">
                        <FLabel color="var(--foam-mute)">
                          {d.startTime ? `${d.startTime}${d.endTime ? `–${d.endTime}` : ''}` : formatDayRange(d)}
                        </FLabel>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-sans font-bold text-[16px]">{d.spotName}</div>
                      <FLabel>
                        {formatDayRange(d)}
                        {d.startTime ? ` · ${d.startTime}` : ''}{d.endTime ? `–${d.endTime}` : ''}
                      </FLabel>
                    </>
                  )}
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    className="font-mono font-bold uppercase"
                    style={{ fontSize: 11, letterSpacing: '0.08em', minWidth: 64,
                      borderLeft: '1.5px solid var(--foam-line)', color: 'var(--foam-ink-2)' }}
                    onClick={() => startEdit(d)}
                  >
                    Edit
                  </button>
                )}
              </div>
            ))}
          </div>
        ))}
      </FScreen>
    );
  }

  // ── Admin: create a demo day + offers ──
  return (
    <FScreen
      header={<FHeader right={<FLabel>{editingId ? 'Edit demo day' : 'New demo day'} · admin</FLabel>} />}
      footer={
        <>
          <FBtn
            primary
            disabled={busy || name.trim().length < 2 || !date
              || (!!endDate && endDate < date)
              || (isPastDay({ date, endDate }) && !pastDateOk)}
            onClick={create}
          >
            {busy ? (editingId ? 'Saving…' : 'Creating…') : (editingId ? 'Save changes' : 'Create demo day')}
          </FBtn>
          {editingId && (
            <FBtn
              ghost
              disabled={busy}
              style={confirmDelete
                ? { borderColor: 'var(--foam-orange)', color: 'var(--foam-orange)', background: '#FFF7F3' }
                : { borderColor: 'var(--foam-orange)', color: 'var(--foam-orange)' }}
              onClick={async () => {
                // Two-tap confirm — deliberate, but no blocking dialog.
                if (!confirmDelete) { setConfirmDelete(true); return; }
                setBusy(true); setError(null);
                try {
                  await deleteDemoDay(editingId);
                  setCreating(false); setEditingId(null); setConfirmDelete(false);
                } catch (e) {
                  setError(e.message || 'Could not delete the demo day.');
                } finally {
                  setBusy(false);
                }
              }}
            >
              {confirmDelete ? 'Tap again to delete this day' : 'Delete this demo day'}
            </FBtn>
          )}
          <FBtn ghost disabled={busy} onClick={() => { setCreating(false); setEditingId(null); setConfirmDelete(false); }}>Back</FBtn>
        </>
      }
    >
      <FHeadline size={40}>{editingId ? 'Edit demo day' : 'New demo day'}</FHeadline>

      <Section label="Where & when">
        <div className="flex flex-col gap-[12px]">
          <div><FLabel>Location</FLabel>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. The Hatch" /></div>
          <div><FLabel>Date</FLabel>
            <Input
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); setPastDateOk(false); }}
              className="w-full font-mono text-[15px] px-[13px]"
            />
            {/* Backfilling a missed day stays possible, but it can't happen by
                tapping through the date field (round 2 §1). */}
            {isPastDay({ date, endDate }) && (
              <button
                type="button"
                onClick={() => setPastDateOk(!pastDateOk)}
                className="flex gap-[12px] items-start text-left w-full mt-[10px]"
                style={{ minHeight: 44, padding: 13,
                  border: `1.5px solid ${pastDateOk ? 'var(--foam-line)' : 'var(--foam-orange)'}`,
                  background: pastDateOk ? 'var(--foam-paper-2)' : '#FFF7F3' }}
              >
                <span
                  className="flex-shrink-0 flex items-center justify-center"
                  style={{ width: 22, height: 22, marginTop: 1,
                    background: pastDateOk ? 'var(--foam-carbon)' : 'var(--foam-paper)',
                    border: '1.5px solid var(--foam-line)',
                    color: 'var(--foam-paper)', fontWeight: 700, fontSize: 13 }}
                >
                  {pastDateOk ? '✓' : ''}
                </span>
                <span className="font-sans text-[13.5px]" style={{ color: 'var(--foam-ink)', lineHeight: 1.5 }}>
                  <strong>{formatDayRange({ date, endDate })} is in the past.</strong>{' '}
                  <span style={{ color: 'var(--foam-ink-2)' }}>
                    Tick to confirm you&apos;re backfilling a day that already happened — staff phones
                    can never pick it up as the live event.
                  </span>
                </span>
              </button>
            )}
          </div>

          {/* Multi-day shows (Nigel 2026-09-08, AWSI runs Wed–Sat). ONE event
              for the whole run: the rack is scanned in once on day one and the
              same boards, sessions and report carry through to the last day.
              Leave blank for an ordinary one-day demo. */}
          <div>
            <div className="flex justify-between items-end">
              <FLabel>Last day</FLabel>
              <FLabel color="var(--foam-mute)">optional — for shows that run several days</FLabel>
            </div>
            <Input
              type="date"
              value={endDate}
              min={date || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full font-mono text-[15px] px-[13px]"
            />
            {endDate && endDate < date && (
              <div className="mt-[8px]">
                <FLabel color="var(--foam-orange)">
                  The last day is before the first — fix it or clear it.
                </FLabel>
              </div>
            )}
            {endDate && endDate > date && (
              <div
                className="mt-[10px] font-sans text-[13px]"
                style={{ border: '1.5px solid var(--foam-line)', background: 'var(--foam-paper-2)',
                  padding: 13, color: 'var(--foam-ink)', lineHeight: 1.5 }}
              >
                <strong>{formatDayRange({ date, endDate })}</strong>
                <span style={{ color: 'var(--foam-ink-2)' }}>
                  {' '}— {dayCount({ date, endDate })} days, one event. Scan the rack in once on the
                  first morning; the boards stay on it all week, and a board still out at midnight
                  can be checked in the next day.
                </span>
              </div>
            )}
          </div>

          <div className="flex gap-[12px]">
            <div className="flex-1" style={{ minWidth: 0 }}><FLabel>Start time</FLabel>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} /></div>
            <div className="flex-1" style={{ minWidth: 0 }}><FLabel>End time</FLabel>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} /></div>
          </div>
        </div>
      </Section>

      <Section
        label="Rider discount"
        hint="The default code. Goes to every rider whose size isn't covered by a retailer below."
      >
        <FLabel>WAKA code</FLabel>
        <Input value={defaultCode} onChange={(e) => setDefaultCode(e.target.value)}
          placeholder="optional" autoCapitalize="characters"
          style={{ fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.04em' }} />
      </Section>

      <div className="flex justify-between items-center">
        <FLabel>Retailer offers</FLabel>
        <FLabel color="var(--foam-mute)">{offers.length} added</FLabel>
      </div>
      {/* Retailer dropdown from the catalogue */}
      <select
        className="w-full font-sans text-[16px] px-[10px]"
        style={{ height: 52, border: '1.5px solid var(--foam-line)', borderRadius: 0, background: '#FFFFFF' }}
        value=""
        onChange={(e) => {
          if (e.target.value === '__new__') { setAddingRetailer(true); return; }
          addOffer(retailers.find((r) => r.id === e.target.value));
        }}
      >
        <option value="">Add a retailer…</option>
        {retailers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        <option value="__new__">+ New retailer (add to catalogue)</option>
      </select>

      {addingRetailer && (
        <div className="flex flex-col gap-[10px]" style={{ background: 'var(--foam-paper-2)', padding: 14 }}>
          <FLabel>New retailer</FLabel>
          <Input value={newRetName} onChange={(e) => setNewRetName(e.target.value)} placeholder="Retailer name" />
          <FLabel>Their WAKA board URLs by size (leave blank if unknown)</FLabel>
          {GLIDE_SIZES.map((sz) => (
            <Input key={sz} value={newRetUrls[sz] || ''} placeholder={`Glide ${sz} URL at this store`}
              onChange={(e) => setNewRetUrls({ ...newRetUrls, [sz]: e.target.value })} />
          ))}
          <FBtn
            height={48}
            disabled={newRetName.trim().length < 2}
            onClick={async () => {
              const urls = Object.fromEntries(Object.entries(newRetUrls).filter(([, v]) => v && v.trim()));
              const id = await saveRetailer({ name: newRetName, boardUrlsBySize: urls }, user);
              const fresh = await listRetailers();
              setRetailers(fresh);
              addOffer(fresh.find((r) => r.id === id));
              setAddingRetailer(false); setNewRetName(''); setNewRetUrls({});
            }}
          >
            Save retailer
          </FBtn>
        </div>
      )}

      {offers.map((o, i) => {
        const missing = o.sizes.filter((sz) => !(o.boardUrlsBySize || {})[sz]);
        return (
          <div key={o.retailerId} style={{ border: '1.5px solid var(--foam-line)', background: 'var(--foam-paper-2)' }}>
            <div className="flex justify-between items-center" style={{ padding: '13px 14px', borderBottom: '1.5px solid var(--foam-line)' }}>
              <FLabel color="var(--foam-mute)">Offer {i + 1} · {o.name}</FLabel>
              <button type="button" style={{ minHeight: 44, fontSize: 20, color: 'var(--foam-ink-2)' }}
                onClick={() => setOffers(offers.filter((x) => x.retailerId !== o.retailerId))}>
                ✕
              </button>
            </div>
            <div className="flex flex-col gap-[12px]" style={{ padding: 14 }}>
              <div>
                <div className="mb-[8px]"><FLabel>In stock at this event</FLabel></div>
                <SizeChips
                  selected={o.sizes}
                  warn={missing}
                  onToggle={(sz) => {
                    const next = [...offers];
                    next[i] = { ...o, sizes: o.sizes.includes(sz) ? o.sizes.filter((x) => x !== sz) : [...o.sizes, sz] };
                    setOffers(next);
                  }}
                />
              </div>
              {missing.length > 0 && (
                <div className="flex gap-[9px] items-start" style={{ background: '#FFF7F3', border: '1px solid var(--foam-orange)', padding: '10px 12px' }}>
                  <span style={{ color: 'var(--foam-orange)', fontWeight: 700, fontSize: 14 }}>!</span>
                  <span className="font-sans text-[12px]" style={{ color: 'var(--foam-ink)', lineHeight: 1.45 }}>
                    No <strong>{o.name}</strong> URL on file for size {missing.join(', ')} — those riders get
                    waka.surf with {o.name}'s code.
                  </span>
                </div>
              )}
              <div>
                <FLabel>Their discount code</FLabel>
                <Input value={o.discountCode} autoCapitalize="characters"
                  style={{ fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.04em' }}
                  onChange={(e) => { const next = [...offers]; next[i] = { ...o, discountCode: e.target.value }; setOffers(next); }} />
              </div>
            </div>
          </div>
        );
      })}

      {error && <FLabel color="var(--foam-orange)">{error}</FLabel>}
      <p className="font-sans text-[12px]" style={{ color: 'var(--foam-mute)' }}>
        Riders who demo a size covered by a retailer offer get that store's link + code in their ride
        email; everyone else gets waka.surf{defaultCode ? ' with the WAKA code' : ''}.
      </p>
    </FScreen>
  );
}
